import { useState, useRef, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { AnimatePresence, motion } from 'framer-motion';
import { auth } from '../../firebase/config';
import { useAuthStore } from '../../store/authStore';
import { useChildStore } from '../../store/childStore';
import { switchToChild } from '../../hooks/useAuth';
import { useT } from '../../i18n';
import { Avatar } from '../avatars/Avatar';

const NAV_LINKS = [
  { to: '/home', key: 'nav.home' },
  { to: '/library', key: 'nav.library' },
  { to: '/parent', key: 'nav.parents' },
] as const;

function navLinkClass(isActive: boolean) {
  return `px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
    isActive ? 'bg-white/10 text-chalk' : 'text-chalk/80 hover:text-chalk hover:bg-white/5'
  }`;
}

export function AppLayout() {
  const { user, setUser } = useAuthStore();
  const { children, activeChild, setActiveChild } = useChildStore();
  const navigate = useNavigate();
  const { t, lang } = useT();

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close dropdown on Escape + restore focus
  useEffect(() => {
    if (!menuOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuTriggerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [menuOpen]);

  // Close mobile menu on Escape
  useEffect(() => {
    if (!mobileNavOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMobileNavOpen(false);
        hamburgerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [mobileNavOpen]);

  const handleLogout = useCallback(async () => {
    setMenuOpen(false);
    setMobileNavOpen(false);
    try {
      await signOut(auth);
    } catch {
      // Demo mode — clear local state anyway
    }
    setUser(null);
    setActiveChild(null);
    navigate('/login', { replace: true });
  }, [setUser, setActiveChild, navigate]);

  // Cycle to the next child and load THAT child's progress — never creates one
  const handleSwitchChild = useCallback(() => {
    setMenuOpen(false);
    const idx = children.findIndex((c) => c.id === activeChild?.id);
    const next = children[(idx + 1) % children.length];
    if (next) void switchToChild(user?.uid, next);
  }, [children, activeChild, user?.uid]);

  const initial = (user?.displayName || user?.email || 'U')[0].toUpperCase();

  return (
    <div className="min-h-screen bg-page flex flex-col">
      {/* Skip link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:bg-teal focus:text-navy focus:px-4 focus:py-2 focus:rounded-xl focus:font-semibold focus:text-sm"
      >
        {lang === 'en' ? 'Skip to main content' : 'Hopp til hovedinnhold'}
      </a>

      {/* Top nav */}
      <header className="border-b border-white/8 bg-navy/95 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Left: hamburger (mobile) + logo */}
          <div className="flex items-center gap-2">
            <button
              ref={hamburgerRef}
              onClick={() => setMobileNavOpen((o) => !o)}
              aria-label={mobileNavOpen ? (lang === 'en' ? 'Close menu' : 'Lukk meny') : (lang === 'en' ? 'Open menu' : 'Åpne meny')}
              aria-expanded={mobileNavOpen}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl text-chalk/80 hover:text-chalk hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-teal/40"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                {mobileNavOpen ? (
                  <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                ) : (
                  <>
                    <path d="M3 5H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M3 10H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M3 15H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </>
                )}
              </svg>
            </button>

            <NavLink to="/home" className="good-atoms-wordmark text-teal text-xl tracking-[0.2em]">
              Good Atoms
            </NavLink>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label={lang === 'en' ? 'Main navigation' : 'Hovednavigasjon'}>
            {NAV_LINKS.map(({ to, key }) => (
              <NavLink key={to} to={to} className={({ isActive }) => navLinkClass(isActive)}>
                {t(key)}
              </NavLink>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Active child pill */}
            {activeChild && (
              <button
                onClick={() => navigate('/home')}
                aria-label={lang === 'en' ? `Go to ${activeChild.name}'s home` : `Gå til ${activeChild.name} sin side`}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/8 hover:bg-white/12 transition-colors"
              >
                <Avatar id={activeChild.avatar} size={28} />
                <span className="text-sm text-chalk/80 font-medium hidden sm:block">
                  {activeChild.name}
                </span>
              </button>
            )}

            {/* Account dropdown */}
            {user && (
              <div className="relative" ref={menuRef}>
                <button
                  ref={menuTriggerRef}
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-label={lang === 'en' ? 'Account menu' : 'Kontomeny'}
                  aria-expanded={menuOpen}
                  aria-haspopup="true"
                  className="w-8 h-8 rounded-full bg-teal/20 border border-teal/30 flex items-center justify-center text-teal text-sm font-semibold hover:bg-teal/30 transition-colors focus:outline-none focus:ring-2 focus:ring-teal/40"
                >
                  {initial}
                </button>

                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      role="menu"
                      aria-label={lang === 'en' ? 'Account menu' : 'Kontomeny'}
                      className="absolute right-0 mt-2 w-56 bg-navy border border-white/10 rounded-2xl shadow-xl overflow-hidden z-50"
                    >
                      {/* User info */}
                      <div className="px-4 py-3 border-b border-white/8">
                        <p className="text-chalk text-sm font-semibold truncate">
                          {user.displayName || (lang === 'en' ? 'Parent account' : 'Foreldrekonto')}
                        </p>
                        <p className="text-chalk/80 text-xs truncate mt-0.5">{user.email}</p>
                      </div>

                      {/* Menu items */}
                      <div className="py-1">
                        <button
                          role="menuitem"
                          onClick={() => { setMenuOpen(false); navigate('/parent'); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-chalk/80 hover:text-chalk hover:bg-white/5 transition-colors flex items-center gap-2.5"
                        >
                          <span aria-hidden="true">👨‍👩‍👧</span>
                          {lang === 'en' ? 'Parent dashboard' : 'Foreldrepanel'}
                        </button>
                        <button
                          role="menuitem"
                          onClick={() => { setMenuOpen(false); navigate('/parent/settings'); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-chalk/80 hover:text-chalk hover:bg-white/5 transition-colors flex items-center gap-2.5"
                        >
                          <span aria-hidden="true">⚙️</span>
                          {lang === 'en' ? 'Settings' : 'Innstillinger'}
                        </button>
                        {children.length > 1 && (
                          <button
                            role="menuitem"
                            onClick={handleSwitchChild}
                            className="w-full text-left px-4 py-2.5 text-sm text-chalk/80 hover:text-chalk hover:bg-white/5 transition-colors flex items-center gap-2.5"
                          >
                            <span aria-hidden="true">🔄</span>
                            {lang === 'en' ? 'Switch child' : 'Bytt barn'}
                          </button>
                        )}
                        <button
                          role="menuitem"
                          onClick={() => { setMenuOpen(false); navigate('/onboarding/child'); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-chalk/80 hover:text-chalk hover:bg-white/5 transition-colors flex items-center gap-2.5"
                        >
                          <span aria-hidden="true">➕</span>
                          {t('parent.add_child')}
                        </button>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-white/8 py-1">
                        <button
                          role="menuitem"
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors flex items-center gap-2.5"
                        >
                          <span aria-hidden="true">↩︎</span>
                          {lang === 'en' ? 'Log out' : 'Logg ut'}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile nav overlay */}
      <AnimatePresence>
        {mobileNavOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileNavOpen(false)}
              className="fixed inset-0 bg-black/40 z-30 md:hidden"
              aria-hidden="true"
            />

            {/* Panel */}
            <motion.nav
              ref={mobileMenuRef}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              aria-label={lang === 'en' ? 'Mobile navigation' : 'Mobilnavigasjon'}
              className="fixed top-16 left-0 right-0 bg-navy border-b border-white/10 z-30 md:hidden shadow-xl"
            >
              <div className="px-4 py-3 flex flex-col gap-1">
                {NAV_LINKS.map(({ to, key }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                        isActive ? 'bg-white/10 text-chalk' : 'text-chalk/80 hover:text-chalk hover:bg-white/5'
                      }`
                    }
                  >
                    {t(key)}
                  </NavLink>
                ))}
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      <main id="main-content" className="flex-1" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}
